import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Toaster, toast } from "sonner";
import "@/App.css";
import { fetchPublished, fetchDraft } from "@/lib/siteConfigStore";
import { checkoutUrlForProduct } from "@/lib/catalogRules";
import { DEFAULT_SITE_CONFIG } from "@/config/defaultSiteConfig";
import { defaultConfigForSlug, publicSiteSlug } from "@/config/siteRegistry";
import { SafeHTML } from "@/components/SafeHTML";

// Product Card Component
const ProductCard = ({ name, description, accent, buttonLabel, buttonUrl, testId }) => {
  const accentClasses = {
    amber: "accent-amber",
    teal: "accent-teal",
    orange: "accent-orange",
    terracotta: "accent-terracotta",
    sky: "accent-sky"
  };

  return (
    <div
      className={`leadline-border bg-clear p-6 sm:p-8 ${accentClasses[accent]} product-card`}
      data-testid={testId}
    >
      <h4 className="text-xl sm:text-2xl mb-3 text-[#3A3530]">{name}</h4>
      <SafeHTML as="div" html={description} className="text-[#3A3530] leading-relaxed text-sm sm:text-base" />
      {buttonLabel && buttonUrl && (
        <a
          href={buttonUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-secondary inline-block mt-4 text-sm"
        >
          {buttonLabel}
        </a>
      )}
    </div>
  );
};

// Art Deco Divider Component
const ArtDecoDivider = () => (
  <div className="art-deco-divider" data-testid="art-deco-divider">
    <div className="art-deco-divider-center"></div>
  </div>
);

// Mosaic Bar Component
const MosaicBar = ({ colors }) => (
  <div className="mosaic-bar" data-testid="mosaic-bar">
    <div style={{ backgroundColor: colors.primary }}></div>
    <div style={{ backgroundColor: colors.secondary }}></div>
    <div style={{ backgroundColor: colors.accentOrange }}></div>
    <div style={{ backgroundColor: colors.accentSky }}></div>
    <div style={{ backgroundColor: colors.accentTerracotta }}></div>
  </div>
);

// Hero Section
const HeroSection = ({ content, sections, colors, introVisible }) => {
  const s = sections?.hero || {};
  const bgColor = colors[s.backgroundColor] || colors.backgroundClear;
  const showBorder = s.showBorder !== false;
  const showCornerAccents = s.showCornerAccents !== false;
  const extraPadding = !introVisible ? "pb-16 sm:pb-24" : "pb-8 sm:pb-12";

  return (
    <section className={`px-4 sm:px-8 lg:px-16 pt-8 sm:pt-12 ${extraPadding}`} data-testid="hero-section">
      <div className="max-w-6xl mx-auto">
        <div
          className={`${showBorder ? "leadline-border" : ""} p-6 sm:p-10 lg:p-16 curve-tr relative`}
          style={{ backgroundColor: bgColor }}
        >
          {showCornerAccents && (
            <>
              <div className="absolute top-0 left-0 w-16 sm:w-24 h-16 sm:h-24 border-r-2 border-b-2 border-[#1E1E1E]"></div>
              <div className="absolute top-0 left-0 w-8 sm:w-12 h-32 sm:h-48 border-r-2 border-[#1E1E1E]"></div>
            </>
          )}
          <div className="text-center pt-8 sm:pt-12">
            <SafeHTML as="h1" html={content.hero.headline} className="text-4xl sm:text-5xl lg:text-7xl tracking-tight text-[#3A3530] mb-4 sm:mb-6" data-testid="hero-headline" />
            <SafeHTML as="div" html={content.hero.tagline} className="text-xl sm:text-2xl lg:text-3xl text-[#3A3530] mb-4 sm:mb-6 font-light" data-testid="hero-tagline" />
            <SafeHTML as="div" html={content.hero.subline} className="text-base sm:text-lg text-[#3A3530] max-w-2xl mx-auto leading-relaxed" data-testid="hero-subline" />
          </div>
        </div>
      </div>
    </section>
  );
};

// Intro Section
const IntroSection = ({ content, sections, colors }) => {
  const s = sections?.intro || {};
  if (s.visible === false) return null;
  const bgColor = colors[s.backgroundColor] || colors.backgroundClear;

  return (
    <section className="px-4 sm:px-8 lg:px-16 pb-16 sm:pb-24" data-testid="intro-section">
      <div className="max-w-5xl mx-auto">
        <div className="leadline-border p-8 sm:p-10 lg:p-12 curve-bl" style={{ backgroundColor: bgColor }}>
          <SafeHTML as="div" html={content.intro.text} className="text-lg sm:text-xl text-[#3A3530] leading-relaxed" data-testid="intro-text" />
        </div>
      </div>
    </section>
  );
};

// What We Make Section
const WhatWeMakeSection = ({ content, products, productGroups, colors, sections }) => {
  const s = sections?.products || {};
  const showDividers = s.showDividers !== false;
  const sortedGroups = [...productGroups].sort((a, b) => a.order - b.order);

  return (
    <section className="px-4 sm:px-8 lg:px-16 pb-16 sm:pb-24" data-testid="what-we-make-section">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8 sm:mb-12">
          <h2 className="section-title text-3xl sm:text-4xl lg:text-5xl text-[#3A3530] mb-4" data-testid="what-we-make-title">
            What we make
          </h2>
          <SafeHTML as="div" html={content.intro.productsSectionIntro} className="text-lg sm:text-xl text-[#3A3530] mt-6 italic" data-testid="what-we-make-intro" />
          {content.intro.shopCtaLabel && content.intro.shopCtaUrl && (
            <a
              href={content.intro.shopCtaUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary inline-block mt-4 text-sm"
              data-testid="shop-cta"
            >
              {content.intro.shopCtaLabel}
            </a>
          )}
        </div>

        {sortedGroups.map((group, index) => {
          const groupItems = products
            .filter((item) => item.group === group.id)
            .sort((a, b) => a.order - b.order);
          const headingColor = colors[group.colorKey];
          const headingTextColor = group.textOnDark ? colors.backgroundClear : colors.textDark;

          return (
            <div key={group.id}>
              <div className="mb-12 sm:mb-16">
                <div
                  className="leadline-border-thin px-4 py-2 mb-6 inline-block"
                  data-testid={`group-${group.id}`}
                  style={{ backgroundColor: headingColor }}
                >
                  <h3 className="text-lg sm:text-xl uppercase tracking-wider" style={{ color: headingTextColor }}>
                    {group.title}
                  </h3>
                </div>
                {group.intro ? (
                  <SafeHTML as="div" html={group.intro} className="text-[#3A3530] mb-6 text-base sm:text-lg" />
                ) : null}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                  {groupItems.map((product) => (
                    <ProductCard
                      key={product.id}
                      name={product.name}
                      description={product.description}
                      accent={product.accent}
                      buttonLabel={product.buttonLabel}
                      buttonUrl={checkoutUrlForProduct(product)}
                      testId={`product-card-${product.name.toLowerCase().replace(/\s+/g, "-")}`}
                    />
                  ))}
                </div>
              </div>
              {showDividers && index < sortedGroups.length - 1 ? <ArtDecoDivider /> : null}
            </div>
          );
        })}

        {s.showMosaicBar && <MosaicBar colors={colors} />}
      </div>
    </section>
  );
};

const accentTopClassByAccent = {
  amber: "accent-top-amber",
  teal: "accent-top-teal",
  orange: "accent-top-orange",
  terracotta: "accent-top-terracotta",
  sky: "accent-top-sky",
};

// Our People Section
const OurPeopleSection = ({ partners }) => (
  <section className="px-4 sm:px-8 lg:px-16 pb-16 sm:pb-24" data-testid="our-people-section">
    <div className="max-w-6xl mx-auto">
      <h2 className="section-title text-3xl sm:text-4xl lg:text-5xl text-[#3A3530] mb-8 sm:mb-12" data-testid="our-people-title">
        Our people
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {partners.map((partner) => (
          <div
            key={partner.id}
            className={`leadline-border bg-[#F5EDE0] p-6 sm:p-8 ${accentTopClassByAccent[partner.accent] || "accent-top-teal"}`}
            data-testid={`partner-${partner.id}`}
          >
            <SafeHTML as="div" html={partner.description} className="text-[#3A3530] leading-relaxed text-base sm:text-lg mb-4" />
            <a
              href={partner.url}
              target="_blank"
              rel="noopener noreferrer"
              className="partner-link text-base sm:text-lg font-medium"
              data-testid={`link-${partner.id}`}
            >
              {partner.linkText}
            </a>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// Footer Section
const FooterSection = ({ content, colors, sections }) => {
  const s = sections?.footer || {};
  const bgColor = colors[s.backgroundColor] || colors.leadline;
  const textColor = colors[s.textColor] || colors.backgroundClear;
  const showMosaicBar = s.showMosaicBar !== false;

  return (
    <footer className="px-4 sm:px-8 lg:px-16 pb-8" data-testid="footer-section">
      <div className="max-w-6xl mx-auto">
        <div className="leadline-border p-8 sm:p-12 lg:p-16 curve-tl curve-br mb-8" style={{ backgroundColor: bgColor }}>
          <SafeHTML as="div" html={content.footer.closingTagline} className="text-2xl sm:text-3xl lg:text-4xl text-center mb-8 sm:mb-12" style={{ color: textColor }} data-testid="footer-tagline" />
          <div className="flex flex-col sm:flex-row justify-center items-center gap-4 sm:gap-6">
            <a
              href={content.footer.signupButtonUrl || "#"}
              className="btn-primary inline-block text-center"
              data-testid="cta-signup"
            >
              {content.footer.signupButtonLabel || "Sign Up for Updates"}
            </a>
            <a
              href={content.footer.shopButtonUrl || "#"}
              className="btn-secondary inline-block text-center"
              data-testid="cta-shop"
            >
              {content.footer.shopButtonLabel || "Browse the Shop"}
            </a>
          </div>
        </div>

        {showMosaicBar && <MosaicBar colors={colors} />}

        <div className="pt-6 pb-4 text-center">
          <p className="text-sm text-[#3A3530]" data-testid="footer-copyright">
            &copy; {new Date().getFullYear()} {content.footer.companyName} — <SafeHTML as="span" html={content.footer.copyrightText} />
          </p>
        </div>
      </div>
    </footer>
  );
};

// Draft Preview Banner
const DraftPreviewBanner = () => (
  <div
    className="bg-[#E05A2D] text-white text-center py-2 text-sm font-medium tracking-wide"
    data-testid="draft-preview-banner"
  >
    Draft Preview — this is unpublished content
  </div>
);

// Main App Component
function App() {
  const [siteConfig, setSiteConfig] = useState(null);
  const [searchParams] = useSearchParams();
  const isPreview = searchParams.get("preview") === "true";
  const previewSlug = publicSiteSlug(searchParams.get("site"));
  const fallbackConfig = isPreview ? defaultConfigForSlug(previewSlug) : DEFAULT_SITE_CONFIG;

  useEffect(() => {
    if (isPreview) {
      fetchDraft(previewSlug)
        .then((result) => setSiteConfig(result?.draft ?? fallbackConfig))
        .catch((err) => {
          console.error("Draft preview failed:", err);
          const code = err.code || "UNKNOWN";
          const message = err.message || "Unknown error";
          const isAuthError =
            code === "42501" ||
            code === "PGRST301" ||
            message.toLowerCase().includes("permission") ||
            message.toLowerCase().includes("auth");
          const isNoRows = code === "PGRST116";
          if (isAuthError) {
            toast.error("Not authenticated — open /admin first to sign in", {
              duration: 8000,
            });
          } else if (isNoRows) {
            toast.error("Draft not found — check that you're signed in and the site config exists", {
              duration: 8000,
            });
          } else {
            toast.error(`Draft load failed: ${message} (${code})`, {
              duration: 8000,
            });
          }
          setSiteConfig(fallbackConfig);
        });
    } else {
      fetchPublished()
        .then(setSiteConfig)
        .catch(() => setSiteConfig(DEFAULT_SITE_CONFIG));
    }
  }, [isPreview, previewSlug, fallbackConfig]);

  useEffect(() => {
    if (!siteConfig) return;
    const { colors, typography } = siteConfig;
    const root = document.documentElement;
    root.style.setProperty("--color-primary", colors.primary);
    root.style.setProperty("--color-secondary", colors.secondary);
    root.style.setProperty("--color-accent-orange", colors.accentOrange);
    root.style.setProperty("--color-accent-terracotta", colors.accentTerracotta);
    root.style.setProperty("--color-accent-sky", colors.accentSky);
    root.style.setProperty("--color-leadline", colors.leadline);
    root.style.setProperty("--color-clear", colors.backgroundClear);
    root.style.setProperty("--color-frosted", colors.backgroundFrosted);
    root.style.setProperty("--color-text", colors.textDark);
    root.style.setProperty("--font-heading", typography.headingFont);
    root.style.setProperty("--font-body", typography.bodyFont);
  }, [siteConfig]);

  if (!siteConfig) {
    return (
      <>
        <Toaster position="top-right" richColors />
        <div className="min-h-screen bg-[#F9F5EE] flex items-center justify-center">
          <h1 className="text-3xl sm:text-4xl text-[#3A3530]" style={{ fontFamily: "Federo, serif" }}>
            578
          </h1>
        </div>
      </>
    );
  }

  const introVisible = siteConfig.sections?.intro?.visible !== false;

  return (
    <>
      <Toaster position="top-right" richColors />
      {isPreview && <DraftPreviewBanner />}
      <div className="landing-page fade-in" data-testid="landing-page">
        <HeroSection
          content={siteConfig.content}
          sections={siteConfig.sections}
          colors={siteConfig.colors}
          introVisible={introVisible}
        />
        <IntroSection content={siteConfig.content} sections={siteConfig.sections} colors={siteConfig.colors} />
        <WhatWeMakeSection
          content={siteConfig.content}
          products={siteConfig.products || []}
          productGroups={siteConfig.productGroups || []}
          colors={siteConfig.colors}
          sections={siteConfig.sections}
        />
        <OurPeopleSection partners={siteConfig.partners} />
        <FooterSection content={siteConfig.content} colors={siteConfig.colors} sections={siteConfig.sections} />
      </div>
    </>
  );
}

export default App;
